import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button as SparkButton, SparkIcon, PopoverMenu } from '@spark';
import { useTaskDataWindowEvents } from '@/features/tasks/hooks/useTaskDataWindowEvents';
import { buildAddDataMenuItems, toPopoverMenuItems } from '@/features/datasets/utils/addDataMenuItems';
import { isAIGenerationDatasetSupported } from '@/features/datasets/utils/aiGenerationDataset';
import { isRetrievalAgentSupported } from '@/features/datasets/utils/retrievalAgentDataset';
import { acceptAttributeForTaskType } from '@/features/datasets/utils/datasetUploadFlow';
import {
    partitionFilesBySupport,
    showUnsupportedFileTypeToast,
} from '@/features/datasets/utils/unsupportedFileHandling';
import { isAgenticAutonomyLevel } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';

const GAP = 4;

/**
 * Unified "Add Data" dropdown button used across the platform.
 *
 * Supports two modes:
 * - **Task-context mode** (`usecase` provided): full menu — Upload Data,
 *   Choose from your uploads, Use Hugging Face, Generate with AI, and
 *   Retrieval Agent (when the task type supports them). "Upload Data" opens
 *   the OS file picker first; after files are chosen it dispatches
 *   `task:add-data` so `TaskPage` opens `AddDataModal` already seeded.
 *   Library / Hugging Face still dispatch immediately; Generate with AI and
 *   Retrieval Agent use dedicated events.
 * - **Standalone mode** (`usecase` is null): upload-only menu — "Upload Data"
 *   still opens the OS picker, then calls `onUploadClick(uploadTarget, files)`.
 *
 * Optional `uploadTarget` (`'dataset'` | `'benchmark'`) is forwarded on the
 * add-data action so Upload / From Library modals can scope which options to show.
 *
 * Optional `iconOnly` renders a compact tertiary "+" icon button (Figma
 * Data Overview file-box header) instead of the labelled button; the menu
 * behavior is unchanged.
 */
const AddDataButton = ({
    usecase = null,
    variant = 'primary',
    size = 'small',
    label = 'Add Data',
    placement = 'bottomRight',
    className = '',
    disabled = false,
    onUploadClick,
    onGenerateAiClick,
    showGenerateAi,
    /** When set (`'dataset'` | `'benchmark'`), scopes Upload / From Library modals. */
    uploadTarget,
    /** Renders a small tertiary "+" icon button instead of the labelled trigger. */
    iconOnly = false,
}) => {
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const fileInputRef = useRef(null);

    const { dispatchTaskDataEvent } = useTaskDataWindowEvents();

    const updatePosition = useCallback(() => {
        const trigger = wrapRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const panelWidth = panelRef.current?.offsetWidth || 240;
        const maxLeft = Math.max(8, window.innerWidth - panelWidth - 8);
        const preferredLeft =
            placement === 'bottomRight' ? rect.right - panelWidth : rect.left;
        setPanelStyle({
            position: 'fixed',
            top: rect.bottom + GAP,
            left: Math.min(maxLeft, Math.max(8, preferredLeft)),
            zIndex: 1400,
        });
    }, [placement]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const id = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(id);
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const handleReposition = () => updatePosition();
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);
        return () => {
            window.removeEventListener('scroll', handleReposition, true);
            window.removeEventListener('resize', handleReposition);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            const target = e.target;
            if (wrapRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    const goToCollect = useCallback(
        (source) => {
            if (!usecase) return;
            dispatchTaskDataEvent(
                'task:add-data',
                {
                    source,
                    usecaseId: usecase._id,
                    ...(uploadTarget ? { uploadTarget } : {}),
                },
                usecase
            );
        },
        [usecase, dispatchTaskDataEvent, uploadTarget]
    );

    const openOsFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleOsFiles = useCallback(
        (fileList) => {
            const files = Array.from(fileList || []).filter(Boolean);
            if (!files.length) return;
            const taskType = usecase?.task_type;
            const formatOptions = { isAgentic: isAgenticAutonomyLevel(usecase?.autonomy_level) };
            const { supported, unsupported } = partitionFilesBySupport(
                files,
                taskType,
                formatOptions
            );
            if (unsupported.length) {
                showUnsupportedFileTypeToast(unsupported, taskType, formatOptions);
            }
            if (!supported.length) return;
            if (usecase) {
                dispatchTaskDataEvent(
                    'task:add-data',
                    {
                        source: 'upload',
                        usecaseId: usecase._id,
                        ...(uploadTarget ? { uploadTarget } : {}),
                        files: supported,
                        file: supported[0],
                    },
                    usecase
                );
                return;
            }
            onUploadClick?.(uploadTarget, supported);
        },
        [usecase, dispatchTaskDataEvent, uploadTarget, onUploadClick]
    );

    const menuItems = useMemo(() => {
        const resolvedShowGenerateAi =
            showGenerateAi !== undefined
                ? showGenerateAi
                : usecase && isAIGenerationDatasetSupported({ taskType: usecase.task_type });
        const taskType = usecase?.task_type;
        return toPopoverMenuItems(
            buildAddDataMenuItems({
                includeUpload: true,
                includeLibrary: Boolean(usecase),
                includeHuggingFace: Boolean(usecase),
                includeGenerateAi: Boolean(resolvedShowGenerateAi),
                includeRetrievalAgent: Boolean(
                    usecase && isRetrievalAgentSupported({ taskType })
                ),
                onUpload: openOsFilePicker,
                onLibrary: () => goToCollect('library'),
                onHuggingFace: () => goToCollect('huggingface'),
                onGenerateAi: () => {
                    if (usecase) {
                        dispatchTaskDataEvent(
                            'task:generate-with-ai',
                            { usecaseId: usecase._id },
                            usecase
                        );
                    } else {
                        onGenerateAiClick?.();
                    }
                },
                onRetrievalAgent: () => {
                    dispatchTaskDataEvent(
                        'task:retrieval-agent',
                        { usecaseId: usecase._id },
                        usecase
                    );
                },
            }),
            { onClose: () => setOpen(false) }
        );
    }, [
        usecase,
        goToCollect,
        dispatchTaskDataEvent,
        onGenerateAiClick,
        showGenerateAi,
        openOsFilePicker,
    ]);

    return (
        <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
            {iconOnly ? (
                <SparkButton
                    className={['fs-unmask', className].filter(Boolean).join(' ')}
                    variant="tertiary"
                    size="small"
                    icon={<SparkIcon slug="plus" size="small" />}
                    iconSlug="plus"
                    tooltip={label}
                    aria-label={label}
                    onClick={() => setOpen((o) => !o)}
                    disabled={disabled}
                    disableWhenReadOnly
                />
            ) : (
                <SparkButton
                    className={['fs-unmask', className].filter(Boolean).join(' ')}
                    variant={variant}
                    size={size}
                    endIcon={<SparkIcon slug="chevron-down" size="small" />}
                    onClick={() => setOpen((o) => !o)}
                    disabled={disabled}
                    disableWhenReadOnly
                >
                    {label}
                </SparkButton>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptAttributeForTaskType(usecase?.task_type, {
                    isAgentic: isAgenticAutonomyLevel(usecase?.autonomy_level),
                })}
                multiple
                data-testid="add-data-os-file-input"
                hidden
                onChange={(e) => {
                    handleOsFiles(e.target.files);
                    e.target.value = '';
                }}
            />
            {open
                ? createPortal(
                    <div
                        ref={panelRef}
                        style={panelStyle}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PopoverMenu tip={false} items={menuItems} />
                    </div>,
                    document.body,
                )
                : null}
        </div>
    );
};

export default AddDataButton;
